import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { RecaptchaComponent as NgRecaptchaComponent } from 'ng-recaptcha-2';
import { RecaptchaErrorParameters } from 'ng-recaptcha-2';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CaptchaService } from '../captcha.service';

@Component({
  selector: 'app-recaptcha',
  templateUrl: './recaptcha.component.html',
  styleUrls: ['./recaptcha.component.scss'],
})
export class RecaptchaComponent implements OnInit, OnDestroy {
  @ViewChild('captchaRef') captchaRef!: NgRecaptchaComponent;
  @Input() siteKey!: string;
  @Output() captchaResolved = new EventEmitter<string>();
  @Output() captchaError = new EventEmitter<void>();
  @Output() captchaExpired = new EventEmitter<void>();

  captchaToken: string | null = null;
  isLoading: boolean = false;
  errorMessage: string | null = null;
  showCaptcha: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(private captchaService: CaptchaService) {}

  ngOnInit(): void {
    this.captchaToken = null;
    this.errorMessage = null;
    this.isLoading = false;

    this.captchaService
      .isCaptchaRequired()
      .pipe(takeUntil(this.destroy$))
      .subscribe((required) => {
        this.showCaptcha = required;

        if (required && !this.isLoading) {
          const currentServiceToken =
            this.captchaService.getCaptchaTokenValue();
          const hasValidToken = this.captchaService.isTokenValid();

          if (hasValidToken && currentServiceToken) {
            this.onCaptchaResolved(currentServiceToken);
          } else {
            setTimeout(() => {
              this.executeCaptcha();
            }, 100);
          }
        }
      });
  }

  executeCaptcha(): void {
    if (!this.captchaRef) {
      console.error('CAPTCHA component reference not available');
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    try {
      this.captchaRef.execute();
    } catch (error) {
      console.error('CAPTCHA execution error:', error);
      this.onCaptchaError({ message: 'Execution failed' });
    }
  }

  resolved(captchaResponse: string): void {
    this.onCaptchaResolved(captchaResponse);
  }

  onCaptchaResolved(token: string): void {
    this.captchaToken = token;
    this.errorMessage = null;
    this.isLoading = false;

    this.captchaService.setCaptchaToken(token);
    this.captchaResolved.emit(token);
  }

  errored(errorDetails: RecaptchaErrorParameters): void {
    this.onCaptchaError(errorDetails);
  }

  onCaptchaError(error: any): void {
    this.errorMessage = 'public.checkout.captcha.verificationFailed';
    this.isLoading = false;
    this.captchaToken = null;

    this.captchaService.setCaptchaToken(null);
    this.captchaError.emit();
  }

  expired(): void {
    this.errorMessage = 'public.checkout.captcha.expired';
    this.captchaToken = null;
    this.captchaService.setCaptchaToken(null);
    this.captchaExpired.emit();
  }

  reset(): void {
    if (this.captchaRef) {
      this.captchaRef.reset();
    }

    this.captchaToken = null;
    this.errorMessage = null;
    this.isLoading = false;

    this.captchaService.resetCaptcha();

    if (this.showCaptcha) {
      setTimeout(() => {
        this.executeCaptcha();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.captchaToken = null;
    this.isLoading = false;
    this.errorMessage = null;
    this.showCaptcha = false;
  }
}
